import { parse } from 'papaparse'
import {
  type AttachmentType,
  type ColumnType,
  type LinkToAnotherRecordType,
  PermissionEntity,
  PermissionKey,
  RelationTypes,
  type TableType,
  UITypes,
  type ViewType,
  isAutoGeneratedColumn,
  isLinksOrLTAR,
  isSystemColumn,
  isVirtualCol,
  populateUniqueFileName,
} from 'nocodb-sdk'
import { generateUniqueColumnName } from '../../../../../helpers/parsers/parserHelpers'
import convertCellData from '../../../../../composables/useMultiSelect/convertCellData'
import type { Cell } from '../../../../../composables/useMultiSelect/cellRange'
import { serializeRange, valueToCopy } from '../../../../../utils/pasteUtils'
import { ComputedTypePasteError } from '../../../../../error/computed-type-paste.error'
import { SelectTypeConversionError } from '../../../../../error/select-type-conversion.error'
import { TypeConversionError } from '../../../../../error/type-conversion.error'
import type { SuppressedError } from '../../../../../error/suppressed.error'
import { EDIT_INTERACTABLE } from '../utils/constants'
import type { ActionManager } from '../loaders/ActionManager'

const MAX_ROWS = 5000

export interface BulkLtarOp {
  columnId: string
  columnTitle: string
  rowRef: Row
  oldValue: any
  data: { operation: string; rowId: string; columnId: string; fk_related_model_id: string }[]
}

export interface TextLtarOp {
  columnId: string
  columnTitle: string
  rowId: string
  rowRef: Row
  oldValue: any
  displayValues: string[]
}

/** OO and OM paste moves records between rows — not suitable for bulk paste */
function isOoOrOm(col: ColumnType): boolean {
  return isOo(col) || (col.colOptions as LinkToAnotherRecordType)?.type === RelationTypes.ONE_TO_MANY
}

export function useCopyPaste({
  activeCell,
  columns,
  scrollToCell,
  selection,
  editEnabled,
  expandRows,
  view,
  meta,
  syncCellData,
  bulkUpsertRows,
  bulkUpdateRows,
  updateOrSaveRow,
  getRows,
  getDataCache,

  actionManager,
}: {
  activeCell: Ref<{
    row?: number
    column?: number
    path?: Array<number>
  }>
  columns: ComputedRef<CanvasGridColumn[]>
  scrollToCell: CanvasScrollToCellFn
  selection: Ref<CellRange>
  editEnabled: Ref<{
    rowIndex: number
    column: ColumnType
    row: Row
    x: number
    y: number
    width: number
    height: number
    path: Array<number>
  } | null>
  expandRows: ({
    newRows,
    newColumns,
    cellsOverwritten,
    rowsUpdated,
  }: {
    newRows: number
    newColumns: number
    cellsOverwritten: number
    rowsUpdated: number
  }) => Promise<{
    continue: boolean
    expand: boolean
  }>
  view: ComputedRef<ViewType | undefined>
  meta: Ref<TableType>
  syncCellData: (ctx: { row: number; column?: number; updatedColumnTitle?: string }, path?: Array<number>) => Promise<void>
  bulkUpsertRows: (
    insertRows: Row[],
    updateRows: Row[],
    props: string[],
    metas?: { metaValue?: TableType; viewMetaValue?: ViewType },
    newColumns?: Partial<ColumnType>[],
    path?: Array<number>,
  ) => Promise<void>
  bulkUpdateRows: (
    rows: Row[],
    props: string[],
    metas?: { metaValue?: TableType; viewMetaValue?: ViewType },
    path?: Array<number>,
  ) => Promise<void>
  updateOrSaveRow: (
    row: Row,
    property?: string,
    ltarState?: Record<string, any>,
    args?: { metaValue?: TableType; viewMetaValue?: ViewType },
    beforeRow?: string,
    path?: Array<number>,
  ) => Promise<any>
  getRows: (start: number, end: number, path?: Array<number>) => Promise<Row[]>
  getDataCache: (path?: Array<number>) => {
    cachedRows: Ref<Map<number, Row>>
    totalRows: Ref<number>
    chunkStates: Ref<Array<'loading' | 'loaded' | undefined>>
    selectedRows: ComputedRef<Array<Row>>
    isRowSortRequiredRows: ComputedRef<Array<Row>>
  }
  actionManager: ActionManager
}) {
  const { $api } = useNuxtApp()
  const { isDataReadOnly } = useRoles()
  const { getMeta, metas } = useMetas()
  const { isMysql, isPg } = useBase()
  const { appInfo } = useGlobal()
  const { t } = useI18n()
  const { isUIAllowed } = useRoles()
  const { copy } = useCopy()
  const { cleaMMCell, clearLTARCell } = useSmartsheetLtarHelpersOrThrow()
  const { isSqlView } = useSmartsheetStoreOrThrow()
  const { isAllowed } = usePermissions()
  const { maxAttachmentsAllowedInCell, showUpgradeToAddMoreAttachmentsInCell } = useEeConfig()
  const { batchUploadFiles } = useAttachment()
  const {
    setCellClipboardDataItem,
    getClipboardItemId,
    getCurrentCopiedCellClipboardData,
    extractCellClipboardData,
    waitingCellClipboardDataIds,
  } = useNcClipboardData()

  const reloadViewDataHook = inject(ReloadViewDataHookInj, createEventHook())
  const isPublic = inject(IsPublicInj, ref(false))

  const { base } = storeToRefs(useBase())
  const fields = computed(() => (columns.value ?? []).map((c) => c.columnObj))

  const hasEditPermission = computed(() => isUIAllowed('dataEdit'))

  const canPasteCell = computed(() => {
    if (isSqlView.value || isPublic.value || !hasEditPermission.value) return false

    return (
      !editEnabled.value ||
      (editEnabled.value &&
        EDIT_INTERACTABLE.includes(editEnabled.value.column?.uidt) &&
        !(activeCell.value.row === -1 || activeCell.value.column === -1))
    )
  })

  function isPasteable(row?: Row, col?: ColumnType, showInfo = false, avoidLtarRestrictions = false) {
    if (!row || !col) {
      if (showInfo) {
        message.toast('Please select a cell to paste')
      }
      return false
    }

    const restrictEditCell = col.id && !isAllowed(PermissionEntity.FIELD, col.id, PermissionKey.RECORD_FIELD_EDIT)

    if (col.readonly && meta.value?.synced && !isAutoGeneratedColumn(col)) {
      if (showInfo) {
        message.toast(t('msg.info.pasteNotSupportedInSyncedCells'))
      }
      return false
    }

    // Skip pasting auto-generated identity columns (AutoNumber, UUID) —
    // values are DB-generated and cannot be user-supplied.
    if (isAutoGeneratedColumn(col)) {
      if (showInfo) {
        message.toast(t('msg.info.computedFieldEditWarning'))
      }
      return false
    }

    // skip pasting virtual columns (including LTAR columns for now) and system columns
    if (isVirtualCol(col) || isSystemColumn(col) || col?.readonly) {
      if (!avoidLtarRestrictions || !isLinksOrLTAR(col)) {
        if (showInfo) {
          message.toast(t('msg.info.pasteNotSupported'))
        }
        return false
      }
    }

    // skip pasting auto increment columns
    if (col.ai) {
      if (showInfo) {
        message.toast(t('msg.info.autoIncFieldNotEditable'))
      }
      return false
    }

    // skip pasting primary key columns
    if (col.pk && !row.rowMeta.new) {
      if (showInfo) {
        message.toast(t('msg.info.editingPKnotSupported'))
      }
      return false
    }

    // Keep this check at the end so that readonly or other field restrictions get priority over permission check
    if (restrictEditCell) {
      if (showInfo) {
        message.toast(t('tooltip.youDoNotHavePermissionToPasteIntoThisField'))
      }
      return false
    }

    return true
  }

  const handlePaste = async (e: ClipboardEvent) => {
    if (!canPasteCell.value) {
      return
    }
    if (!meta.value?.id) return

    if (
      isDrawerOrModalExist() ||
      isExpandedCellInputExist() ||
      isLinkDropdownExist() ||
      isViewSearchActive() ||
      isSidebarNodeRenameActive() ||
      isActiveElementInsideExtension() ||
      isActiveElementInsideScriptPane() ||
      isActiveElementInsideSmartTextPanel() ||
      isCmdJActive() ||
      cmdKActive()
    ) {
      return
    }
    if (isNcDropdownOpen()) return

    e.preventDefault()

    const groupPath = activeCell.value.path
    const dataCache = getDataCache(groupPath)

    const { totalRows, cachedRows } = dataCache

    // Replace \" with " in clipboard data
    let clipboardData = e.clipboardData?.getData('text/plain') || ''

    const storedCopiedData = getCurrentCopiedCellClipboardData(clipboardData)

    // Add the waiting clipboard data id so that if setClipboardData fn is called during this operation, it will not remove the currentCopiedCellClipboardDataItem from the clipboard data
    if (storedCopiedData && !waitingCellClipboardDataIds.value.includes(storedCopiedData.id)) {
      waitingCellClipboardDataIds.value.push(storedCopiedData.id)
    }

    if (clipboardData?.endsWith('\n')) {
      // Remove '\n' from the end of the clipboardData
      // When copying from XLS/XLSX files, there is an extra '\n' appended to the end
      //   this overwrites one additional cell information when we paste in NocoDB
      clipboardData = clipboardData.replace(/\n$/, '')
    }

    try {
      const isMultiCellSelection = !selection.value.isSingleCell()

      if (clipboardData?.includes('\n') || clipboardData?.includes('\t') || isMultiCellSelection) {
        // if the clipboard data contains new line or tab, then it is a matrix or LongText
        // Also handle single value pasted into multi-cell selection (e.g. text into multiple LTAR cells)

        let clipboardMatrix: string[][]

        if (clipboardData?.includes('\n') || clipboardData?.includes('\t')) {
          const parsedClipboard = parse(clipboardData, {
            delimiter: '\t',
            escapeChar: '\\',
          })

          if (parsedClipboard.errors.length > 0) {
            return message.error(parsedClipboard.errors[0]?.message)
          }

          clipboardMatrix = parsedClipboard.data as string[][]
        } else {
          clipboardMatrix = [[clipboardData]]
        }

        // Special handling for "null" values - convert literal "null" strings to empty strings
        // This ensures that empty cells from numeric fields don't appear as "null" text
        clipboardMatrix = clipboardMatrix.map((row) => row.map((cell) => (cell === 'null' ? '' : cell)))

        let isTruncated = false
        if (clipboardMatrix.length > MAX_ROWS) {
          clipboardMatrix = clipboardMatrix.slice(0, MAX_ROWS)
          isTruncated = true
        }

        const selectionRowCount = Math.max(clipboardMatrix.length, selection.value.end.row - selection.value.start.row + 1)

        const pasteMatrixCols = clipboardMatrix[0]?.length || 0
        const startColIndex = activeCell.value.column
        const existingFields = unref(fields)
        const existingColCount = existingFields.length - startColIndex
        const newColsNeeded = Math.max(0, pasteMatrixCols - existingColCount)

        const tempTotalRows = totalRows.value
        const totalRowsBeforeActiveCell = selection.value.start.row
        const availableRowsToUpdate = Math.max(0, tempTotalRows - totalRowsBeforeActiveCell)
        const rowsToAdd = Math.max(0, selectionRowCount - availableRowsToUpdate)

        // M2M junction tables don't support inserting rows/columns via paste —
        // records are owned by the link cell, not the junction directly.
        const isMmTable = !!meta.value?.mm

        // Check if expansion is needed
        let options = {
          continue: false,
          expand: (rowsToAdd > 0 || newColsNeeded > 0) && !isMmTable,
        }

        if (options.expand) {
          options = await expandRows?.({
            newRows: rowsToAdd,
            newColumns: newColsNeeded,
            cellsOverwritten: Math.min(availableRowsToUpdate, selectionRowCount) * (pasteMatrixCols - newColsNeeded),
            rowsUpdated: Math.min(availableRowsToUpdate, selectionRowCount),
          })
          if (!options.continue) return
        }
        // Handle column operations
        let colsToPaste
        const bulkOpsCols = []

        if (options.expand) {
          colsToPaste = fields.value.slice(selection.value.start.col, selection.value.start.col + pasteMatrixCols)
          if (newColsNeeded > 0) {
            const columnsHash = (
              await $api.internal.getOperation(meta.value!.fk_workspace_id!, meta.value!.base_id!, {
                operation: 'columnsHash',
                tableId: meta.value?.id as string,
              })
            ).hash
            const columnsLength = meta.value?.columns?.length || 0

            // Create new columns as needed
            for (let i = 0; i < newColsNeeded; i++) {
              const tempCol = {
                uidt: UITypes.SingleLineText,
                order: columnsLength + i,
                column_order: {
                  order: columnsLength + i,
                  view_id: view.value?.id,
                },
                view_id: view.value?.id,
                table_name: meta.value?.table_name,
              }

              const newColTitle = generateUniqueColumnName({
                metaColumns: [...(meta.value?.columns ?? []), ...bulkOpsCols.map(({ column }) => column)],
                formState: tempCol,
              })

              bulkOpsCols.push({
                op: 'add',
                column: {
                  ...tempCol,
                  title: newColTitle,
                },
              })
            }

            await $api.internal.postOperation(
              meta.value!.fk_workspace_id!,
              meta.value!.base_id!,
              { operation: 'columnsBulk', tableId: meta.value?.id as string },
              {
                hash: columnsHash,
                ops: bulkOpsCols,
              },
            )

            await getMeta(meta?.value?.base_id as string, meta?.value?.id as string, true)
            colsToPaste = [...colsToPaste, ...bulkOpsCols.map(({ column }) => column)]
          }
        } else {
          colsToPaste = fields.value.slice(selection.value.start.col, selection.value.start.col + pasteMatrixCols)
        }

        await getRows(selection.value.start.row, selection.value.start.row + clipboardMatrix.length, groupPath)

        const dataRef = unref(cachedRows)

        const updatedRows: Row[] = [] as Row[]
        const newRows: Row[] = []
        const propsToPaste: string[] = []
        const bulkLtarOps: BulkLtarOp[] = []
        const textLtarOps: TextLtarOp[] = []
        let isInfoShown = false
        // We can use this if we want to avoid same info multiple times per column
        const isColInfoShown = {} as Record<string, boolean>

        for (let i = 0; i < selectionRowCount; i++) {
          const clipboardRowIndex = i % clipboardMatrix.length
          let targetRow: any

          if (i < availableRowsToUpdate) {
            const absoluteRowIndex = totalRowsBeforeActiveCell + i
            targetRow = deepClone(dataRef.get(absoluteRowIndex)) || {
              row: {},
              oldRow: {},
              rowMeta: {
                isExistingRow: true,
                rowIndex: absoluteRowIndex,
              },
            }

            // Skip rows hidden by RLS policy — they are locked and should not be pasted into
            if (targetRow.rowMeta?.isRlsHidden) continue

            updatedRows.push(targetRow)
          } else {
            targetRow = {
              row: {},
              oldRow: {},
              rowMeta: {
                isExistingRow: false,
              },
            }
            newRows.push(targetRow)
          }

          // Process each cell in the row
          if (!clipboardMatrix[clipboardRowIndex]) continue
          for (let j = 0; j < clipboardMatrix[clipboardRowIndex].length; j++) {
            const column = colsToPaste[j]
            if (!column) continue

            const isBtTextTargetCol = isBt(column) && !(column.meta as any)?.custom
            const isLtarTextTargetCol = isBtTextTargetCol || isMMOrMMLike(column)

            // Collect junction-table-based LTAR columns for bulk API — and route v1 BT text paste to the same API
            if (isLtarTextTargetCol) {
              if (!isPasteable(targetRow, column, false, true)) continue

              const clipboardItem = extractCellClipboardData(storedCopiedData, clipboardRowIndex, j)
              const isStructuredLtarPaste = clipboardItem && isLinksOrLTAR(clipboardItem.column)

              if (isStructuredLtarPaste) {
                // Structured bulk paste is only supported for junction-based LTAR columns — v1 BT has no matching source
                if (!isMMOrMMLike(column)) continue

                // Block structured copy-paste for OO and OM — paste moves records between rows
                if (isOoOrOm(column)) {
                  if (!isInfoShown) {
                    message.toast(t('msg.info.groupPasteIsNotSupportedOnOoOmColumn'))
                    isInfoShown = true
                  }
                  continue
                }

                const pasteVal = convertCellData(
                  {
                    value: clipboardMatrix[clipboardRowIndex][j],
                    to: column.uidt as UITypes,
                    column,
                    appInfo: unref(appInfo),
                    clipboardItem,
                  },
                  isMysql(meta.value?.source_id),
                )

                if (pasteVal === undefined || !ncIsObject(pasteVal)) continue

                const pasteRowPk = extractPkFromRow(targetRow.row, meta.value?.columns as ColumnType[])
                if (!pasteRowPk) continue

                const oldValue = targetRow.row[column.title!]
                targetRow.row[column.title!] = pasteVal.value

                bulkLtarOps.push({
                  columnId: column.id as string,
                  columnTitle: column.title!,
                  rowRef: targetRow,
                  oldValue,
                  data: [
                    {
                      operation: 'copy',
                      rowId: pasteVal.rowId,
                      columnId: pasteVal.columnId,
                      fk_related_model_id: pasteVal.fk_related_model_id,
                    },
                    {
                      operation: 'paste',
                      rowId: pasteRowPk,
                      columnId: column.id as string,
                      fk_related_model_id:
                        (column.colOptions as LinkToAnotherRecordType).fk_related_model_id || pasteVal.fk_related_model_id,
                    },
                  ],
                })
              } else {
                // Plain text paste — resolve display values to linked records
                const plainText = clipboardMatrix[clipboardRowIndex][j]
                if (!plainText?.trim()) continue

                const displayValues = plainText
                  .split(',')
                  .map((v: string) => v.trim())
                  .filter(Boolean)
                if (!displayValues.length) continue

                const pasteRowPk = extractPkFromRow(targetRow.row, meta.value?.columns as ColumnType[])
                if (!pasteRowPk) continue

                textLtarOps.push({
                  columnId: column.id as string,
                  columnTitle: column.title!,
                  rowId: pasteRowPk,
                  rowRef: targetRow,
                  oldValue: targetRow.row[column.title!],
                  displayValues,
                })
              }
              continue
            }

            if (isPasteable(targetRow, column)) {
              propsToPaste.push(column.title!)
              let pasteValue: any
              try {
                pasteValue = convertCellData(
                  {
                    value: clipboardMatrix[clipboardRowIndex][j],
                    to: column.uidt as UITypes,
                    column,
                    appInfo: unref(appInfo),
                    oldValue: column.uidt === UITypes.Attachment ? targetRow.row[column.title!] : undefined,
                    maxAttachmentsAllowedInCell: maxAttachmentsAllowedInCell.value,
                    showUpgradeToAddMoreAttachmentsInCell,
                    isInfoShown: isColInfoShown[column.title!],
                    markInfoShown: () => {
                      isColInfoShown[column.title!] = true
                    },
                    clipboardItem: extractCellClipboardData(storedCopiedData, clipboardRowIndex, j),
                  },
                  isMysql(meta.value?.source_id),
                  true,
                )
                validateColumnValue(column, pasteValue)
              } catch (ex) {
                if (ex instanceof ComputedTypePasteError) {
                  throw ex
                } else if (ex instanceof SelectTypeConversionError) {
                  // Backend `typecast=true` (default on data save APIs) creates
                  // the missing options server-side as part of the same request.
                  pasteValue = ex.value.join(',')
                } else if (ex instanceof TypeConversionError) {
                  pasteValue = null
                } else throw ex
              }

              if (pasteValue !== undefined) {
                targetRow.row[column.title!] = pasteValue
              }
            }
          }
        }

        // Execute bulk LTAR paste operations in a single API call
        if (bulkLtarOps.length) {
          try {
            await $api.internal.postOperation(
              meta.value?.fk_workspace_id as string,
              meta.value?.base_id as string,
              {
                operation: 'nestedDataBulkCopyPasteOrDeleteAll',
                tableId: meta.value?.id as string,
                viewId: view?.value?.id,
              },
              bulkLtarOps.map(({ columnId, data }) => ({ columnId, data })),
            )
          } catch (e: any) {
            for (const op of bulkLtarOps) {
              op.rowRef.row[op.columnTitle] = op.oldValue
            }
            message.error({
              title: t('msg.error.pasteFromClipboardError'),
              content: await extractSdkResponseErrorMsg(e),
            })
          }
        }

        // Execute text-based LTAR link-by-display-value operations
        if (textLtarOps.length) {
          try {
            await $api.internal.postOperation(
              meta.value?.fk_workspace_id as string,
              meta.value?.base_id as string,
              {
                operation: 'nestedDataBulkLinkByDisplayValue',
                tableId: meta.value?.id as string,
                viewId: view?.value?.id,
              },
              textLtarOps.map(({ columnId, rowId, displayValues }) => ({ columnId, rowId, displayValues })),
            )

            reloadViewDataHook?.trigger({ shouldShowLoading: false })
          } catch (e: any) {
            for (const op of textLtarOps) {
              op.rowRef.row[op.columnTitle] = op.oldValue
            }
            message.error(await extractSdkResponseErrorMsg(e))
          }
        }

        if (options.expand) {
          await bulkUpsertRows?.(
            newRows,
            updatedRows,
            propsToPaste,
            undefined,
            bulkOpsCols.map(({ column }) => column),
            groupPath,
          )
          scrollToCell?.(undefined, undefined, groupPath)
        } else {
          await bulkUpdateRows?.(updatedRows, propsToPaste, undefined, groupPath)
        }

        if (isTruncated) {
          message.warning(t('tooltip.pasteOperationLimitedToMaxRows', { max: MAX_ROWS }))
        }
      } else {
        if (selection.value.isSingleCell()) {
          const rowObj = (unref(cachedRows) as Map<number, Row>).get(activeCell.value.row)
          const columnObj = unref(fields)[activeCell.value.column]

          if (!rowObj || !columnObj) return

          const singleCellClipboardItem = extractCellClipboardData(storedCopiedData, 0, 0)
          const isStructuredLtarPaste = singleCellClipboardItem && isLinksOrLTAR(singleCellClipboardItem.column)

          const isBtTextTarget = isBt(columnObj) && !(columnObj.meta as any)?.custom
          const isLtarTextTarget = isBtTextTarget || isMMOrMMLike(columnObj)

          // Plain text paste into LTAR (BT v1/v2, MM) — resolve display values to linked records
          if (isLtarTextTarget && !isStructuredLtarPaste) {
            if (!isPasteable(rowObj, columnObj, true, true)) return

            const plainText = clipboardData
            if (!plainText?.trim()) return

            const displayValues = plainText
              .split(',')
              .map((v: string) => v.trim())
              .filter(Boolean)
            if (!displayValues.length) return

            const pasteRowPk = extractPkFromRow(rowObj.row, meta.value?.columns as ColumnType[])
            if (!pasteRowPk) return

            try {
              await $api.internal.postOperation(
                meta.value?.fk_workspace_id as string,
                meta.value?.base_id as string,
                {
                  operation: 'nestedDataBulkLinkByDisplayValue',
                  tableId: meta.value?.id as string,
                  viewId: view?.value?.id,
                },
                [{ columnId: columnObj.id as string, rowId: pasteRowPk, displayValues }],
              )

              // Refresh view data so lookup/rollup columns reflect the new links
              reloadViewDataHook?.trigger({ shouldShowLoading: false })

              return await syncCellData?.(activeCell.value, groupPath)
            } catch (e: any) {
              message.error(await extractSdkResponseErrorMsg(e))
              return
            }
          }

          // handle belongs to column, skip custom links — structured paste only (text path handled above)
          if (isBtTextTarget) {
            if (!isPasteable(rowObj, columnObj, true, true)) return

            const pasteVal = convertCellData(
              {
                value: clipboardData,
                to: columnObj.uidt as UITypes,
                column: columnObj,
                appInfo: unref(appInfo),
                clipboardItem: singleCellClipboardItem,
              },
              isMysql(meta.value?.source_id),
            )

            if (pasteVal === undefined || !ncIsObject(pasteVal)) return

            const foreignKeyColumn = meta.value?.columns?.find(
              (column: ColumnType) => column.id === (columnObj.colOptions as LinkToAnotherRecordType)?.fk_child_column_id,
            )

            if (!foreignKeyColumn) return

            const relatedBaseId =
              (columnObj.colOptions as LinkToAnotherRecordType as any)?.fk_related_base_id || meta?.value?.base_id
            const relatedTableMeta = await getMeta(
              relatedBaseId as string,
              (columnObj.colOptions as LinkToAnotherRecordType).fk_related_model_id!,
            )

            // update old row to allow undo redo as bt column update only through foreignKeyColumn title
            rowObj.oldRow[columnObj.title!] = rowObj.row[columnObj.title!]
            rowObj.oldRow[foreignKeyColumn.title!] = rowObj.row[columnObj.title!]
              ? extractPkFromRow(rowObj.row[columnObj.title!], (relatedTableMeta as any)!.columns!)
              : null

            rowObj.row[columnObj.title!] = pasteVal?.value

            rowObj.row[foreignKeyColumn.title!] = pasteVal?.value
              ? extractPkFromRow(pasteVal.value, (relatedTableMeta as any)!.columns!)
              : null

            return await syncCellData?.({ ...activeCell.value, updatedColumnTitle: foreignKeyColumn.title }, groupPath)
          }

          // Handle junction-table-based LTAR column paste (V1 MM, V2 om/mo/oo/mm) — structured only
          if (isMMOrMMLike(columnObj)) {
            if (!isPasteable(rowObj, columnObj, true, true)) return

            const pasteVal = convertCellData(
              {
                value: clipboardData,
                to: columnObj.uidt as UITypes,
                column: columnObj,
                appInfo: unref(appInfo),
                clipboardItem: singleCellClipboardItem,
              },
              isMysql(meta.value?.source_id),
            )

            if (pasteVal === undefined || !ncIsObject(pasteVal)) return

            const pasteRowPk = extractPkFromRow(rowObj.row, meta.value?.columns as ColumnType[])
            if (!pasteRowPk) return

            const oldCellValue = rowObj.row[columnObj.title!]

            rowObj.row[columnObj.title!] = pasteVal.value

            let result

            try {
              result = await $api.internal.postOperation(
                meta.value?.fk_workspace_id as string,
                meta.value?.base_id as string,
                {
                  operation: 'nestedDataListCopyPasteOrDeleteAll',
                  tableId: meta.value?.id as string,
                  columnId: columnObj.id as string,
                  viewId: view?.value?.id,
                },
                [
                  {
                    operation: 'copy',
                    rowId: pasteVal.rowId,
                    columnId: pasteVal.columnId,
                    fk_related_model_id: pasteVal.fk_related_model_id,
                  },
                  {
                    operation: 'paste',
                    rowId: pasteRowPk,
                    columnId: columnObj.id as string,
                    fk_related_model_id:
                      (columnObj.colOptions as LinkToAnotherRecordType).fk_related_model_id || pasteVal.fk_related_model_id,
                  },
                ],
              )
            } catch {
              rowObj.row[columnObj.title!] = oldCellValue
              return
            }

            if (result && result?.link && result?.unlink && Array.isArray(result.link) && Array.isArray(result.unlink)) {
              if (!result.link.length && !result.unlink.length) {
                rowObj.row[columnObj.title!] = oldCellValue
                return
              }

              // For OO and OM columns, pasting is a "move" — the backend enforces that each
              // child/record can only belong to one parent. Refresh view to reflect changes across all rows.
              // For MM and other LTAR types, refresh the view so dependent lookup/rollup columns update.
              reloadViewDataHook?.trigger({ shouldShowLoading: false })
            }

            return await syncCellData?.(activeCell.value, groupPath)
          }

          if (!isPasteable(rowObj, columnObj, true)) {
            return
          }

          let pasteValue: any

          try {
            pasteValue = convertCellData(
              {
                value: clipboardData,
                to: columnObj.uidt as UITypes,
                column: columnObj,
                appInfo: unref(appInfo),
                files:
                  columnObj.uidt === UITypes.Attachment && e.clipboardData?.files?.length ? e.clipboardData?.files : undefined,
                oldValue: rowObj.row[columnObj.title!],
                maxAttachmentsAllowedInCell: maxAttachmentsAllowedInCell.value,
                showUpgradeToAddMoreAttachmentsInCell,
                clipboardItem: extractCellClipboardData(storedCopiedData, 0, 0),
              },
              isMysql(meta.value?.source_id),
            )
            validateColumnValue(columnObj, pasteValue)
          } catch (ex) {
            if (ex instanceof ComputedTypePasteError) {
              throw ex
            } else if (ex instanceof SelectTypeConversionError) {
              // Backend `typecast=true` (default on data save APIs) creates
              // the missing options server-side as part of the row update.
              pasteValue = ex.value.join(',')
            } else if (ex instanceof TypeConversionError) {
              pasteValue = null
            } else throw ex
          }

          if (columnObj.uidt === UITypes.Attachment && e.clipboardData?.files?.length && pasteValue?.length) {
            const pasteRowPk = extractPkFromRow(rowObj.row, meta.value?.columns as ColumnType[])

            const uploadAction = async () => {
              const newAttachments =
                (await handleFileUploadAndGetCellValue(pasteValue, columnObj.id!, rowObj.row[columnObj.title!])) || []

              const oldAttachments = ncIsArray(rowObj.row[columnObj.title!]) ? rowObj.row[columnObj.title!] : []

              rowObj.row[columnObj.title!] =
                newAttachments.length || oldAttachments.length ? JSON.stringify(oldAttachments.concat(newAttachments)) : null
            }

            if (pasteRowPk) {
              await actionManager.executeUploadAction(pasteRowPk, columnObj.id!, [], uploadAction)
            } else {
              await uploadAction()
            }
          } else if (pasteValue !== undefined) {
            rowObj.row[columnObj.title!] = pasteValue
          }

          await syncCellData?.(activeCell.value, groupPath)
        } else {
          const { start, end } = selection.value

          const startRow = Math.min(start.row, end.row)
          const endRow = Math.max(start.row, end.row)
          const startCol = Math.min(start.col, end.col)
          const endCol = Math.max(start.col, end.col)

          const rows = await getRows(startRow, endRow, groupPath)
          const cols = unref(fields).slice(startCol, endCol + 1)
          const props = []

          let pasteValue
          let rangeBulkLtarOpsInfoShown = false
          // We can use this if we want to avoid same info multiple times per column
          const isColInfoShown = {} as Record<string, boolean>
          const rangeBulkLtarOps: BulkLtarOp[] = []

          const files = e.clipboardData?.files

          for (const row of rows) {
            // TODO handle insert new row
            if (!row || row.rowMeta.new) continue

            for (const col of cols) {
              // Collect junction-table-based LTAR columns for bulk API
              if (isMMOrMMLike(col)) {
                // Block bulk paste for OO and OM — paste moves records between rows, not suitable for bulk
                if (isOoOrOm(col)) {
                  if (!rangeBulkLtarOpsInfoShown) {
                    message.toast(t('msg.info.groupPasteIsNotSupportedOnOoOmColumn'))
                    rangeBulkLtarOpsInfoShown = true
                  }
                  continue
                }

                if (!isPasteable(row, col, false, true)) continue

                const ltarPasteVal = convertCellData(
                  {
                    value: clipboardData,
                    to: col.uidt as UITypes,
                    column: col,
                    appInfo: unref(appInfo),
                    clipboardItem: extractCellClipboardData(storedCopiedData, 0, 0),
                  },
                  isMysql(meta.value?.source_id),
                )

                if (ltarPasteVal !== undefined && ncIsObject(ltarPasteVal)) {
                  const pasteRowPk = extractPkFromRow(row.row, meta.value?.columns as ColumnType[])
                  if (pasteRowPk) {
                    const oldValue = row.row[col.title!]
                    row.row[col.title!] = ltarPasteVal.value

                    rangeBulkLtarOps.push({
                      columnId: col.id as string,
                      columnTitle: col.title!,
                      rowRef: row,
                      oldValue,
                      data: [
                        {
                          operation: 'copy',
                          rowId: ltarPasteVal.rowId,
                          columnId: ltarPasteVal.columnId,
                          fk_related_model_id: ltarPasteVal.fk_related_model_id,
                        },
                        {
                          operation: 'paste',
                          rowId: pasteRowPk,
                          columnId: col.id as string,
                          fk_related_model_id:
                            (col.colOptions as LinkToAnotherRecordType).fk_related_model_id || ltarPasteVal.fk_related_model_id,
                        },
                      ],
                    })
                  }
                }
                continue
              }

              if (!col.title || !isPasteable(row, col)) {
                continue
              }

              if (files?.length) {
                if (col.uidt !== UITypes.Attachment) {
                  continue
                }

                if (pasteValue === undefined) {
                  const fileUploadPayload = convertCellData(
                    {
                      value: '',
                      to: col.uidt as UITypes,
                      column: col,
                      appInfo: unref(appInfo),
                      files,
                      oldValue: row.row[col.title],
                      maxAttachmentsAllowedInCell: maxAttachmentsAllowedInCell.value,
                      showUpgradeToAddMoreAttachmentsInCell,
                      isInfoShown: isColInfoShown[col.title!],
                      markInfoShown: () => {
                        isColInfoShown[col.title!] = true
                      },
                      clipboardItem: extractCellClipboardData(storedCopiedData, 0, 0),
                    },
                    isMysql(meta.value?.source_id),
                    true,
                  )

                  if (fileUploadPayload?.length) {
                    const uploadAction = async () => {
                      const newAttachments = await handleFileUploadAndGetCellValue(
                        fileUploadPayload,
                        col.id!,
                        row.row[col.title!],
                      )

                      pasteValue = newAttachments ? JSON.stringify(newAttachments) : null
                    }

                    const pasteRowsPk = rows
                      .map((row) => {
                        return extractPkFromRow(row.row, meta.value?.columns as ColumnType[])
                      })
                      .filter(Boolean) as string[]

                    if (pasteRowsPk.length) {
                      // We do upload action for first row only and use uploaded url in other rows
                      await actionManager.executeUploadAction(pasteRowsPk, col.id!, [], uploadAction)
                    } else {
                      await uploadAction()
                    }
                  }
                }
              } else {
                try {
                  pasteValue = convertCellData(
                    {
                      value: clipboardData,
                      to: col.uidt as UITypes,
                      column: col,
                      appInfo: unref(appInfo),
                      oldValue: row.row[col.title],
                      maxAttachmentsAllowedInCell: maxAttachmentsAllowedInCell.value,
                      showUpgradeToAddMoreAttachmentsInCell,
                      isInfoShown: isColInfoShown[col.title!],
                      markInfoShown: () => {
                        isColInfoShown[col.title!] = true
                      },
                      clipboardItem: extractCellClipboardData(storedCopiedData, 0, 0),
                    },
                    isMysql(meta.value?.source_id),
                    true,
                  )
                  validateColumnValue(col, pasteValue)
                } catch (ex) {
                  if (ex instanceof ComputedTypePasteError) {
                    throw ex
                  } else if (ex instanceof SelectTypeConversionError) {
                    // Backend `typecast=true` (default on data save APIs) creates
                    // the missing options server-side as part of the same request.
                    pasteValue = ex.value.join(',')
                  } else if (ex instanceof TypeConversionError) {
                    pasteValue = null
                  } else throw ex
                }
              }

              props.push(col.title)

              if (pasteValue !== undefined) {
                row.row[col.title] = pasteValue
              }
            }
          }

          // Execute bulk LTAR paste operations in a single API call
          if (rangeBulkLtarOps.length) {
            try {
              await $api.internal.postOperation(
                meta.value?.fk_workspace_id as string,
                meta.value?.base_id as string,
                {
                  operation: 'nestedDataBulkCopyPasteOrDeleteAll',
                  tableId: meta.value?.id as string,
                  viewId: view?.value?.id,
                },
                rangeBulkLtarOps.map(({ columnId, data }) => ({ columnId, data })),
              )
            } catch (e: any) {
              for (const op of rangeBulkLtarOps) {
                op.rowRef.row[op.columnTitle] = op.oldValue
              }
              message.error({
                title: t('msg.error.pasteFromClipboardError'),
                content: await extractSdkResponseErrorMsg(e),
              })
            }
          }

          if (props.length) {
            await bulkUpdateRows?.(rows, props, undefined, groupPath)
          }
        }
      }
    } catch (error: any) {
      if (error instanceof TypeConversionError !== true || !(error as SuppressedError).isErrorSuppressed) {
        console.error(error, (error as SuppressedError).isErrorSuppressed)
        message.error(await extractSdkResponseErrorMsg(error))
      }
    } finally {
      // After paste operation is completed, remove the waiting clipboard data id so that on setClipboardDateItem can remove the item from the clipboard data
      if (storedCopiedData && waitingCellClipboardDataIds.value.includes(storedCopiedData.id)) {
        waitingCellClipboardDataIds.value = waitingCellClipboardDataIds.value.filter((id) => id !== storedCopiedData.id)
      }
    }
  }

  async function handleFileUploadAndGetCellValue(files: File[], columnId: string, oldValue: AttachmentType[]) {
    const newAttachments: AttachmentType[] = []

    try {
      const data = await batchUploadFiles(files, [NOCO, base.value.id, meta.value?.id, columnId].join('/'))

      // add suffix in duplicate file title
      for (const uploadedFile of data) {
        newAttachments.push({
          ...uploadedFile,
          title: populateUniqueFileName(
            uploadedFile?.title,
            [...handleParseAttachmentCellData(oldValue), ...newAttachments].map((fn) => fn?.title || (fn as any)?.fileName),
          ),
        })
      }
      return newAttachments
    } catch (e: any) {
      message.error((await extractSdkResponseErrorMsg(e)) || t('msg.error.internalError'))
    }
  }

  const copyTable = async (rows: Row[], cols: ColumnType[]) => {
    const {
      html: copyHTML,
      text: copyPlainText,
      clipboardItemConfig,
    } = serializeRange(rows, cols, {
      meta: meta.value,
      isPg,
      isMysql,
    })

    const blobHTML = new Blob([copyHTML], { type: 'text/html' })
    const blobPlainText = new Blob([copyPlainText], { type: 'text/plain' })

    const clipboardItem: NcClipboardDataItemType = {
      ...clipboardItemConfig,
      tableId: meta.value?.id,
      id: getClipboardItemId(),
    }

    const res = await (navigator.clipboard?.write([
      new ClipboardItem({ [blobHTML.type]: blobHTML, [blobPlainText.type]: blobPlainText }),
    ]) ?? copy(copyPlainText))

    setCellClipboardDataItem(clipboardItem)

    return res
  }

  async function clearCell(ctx: { row: number; col: number; path?: Array<number> } | null, skipUpdate = false) {
    // If the data is readonly, return
    // If the cell is not available, return
    // If the user doesn't have edit permission, return
    // If the cell is a virtual column and not Links/Ltar, return
    if (!ctx) return

    const groupPath = ctx?.path

    const dataCache = getDataCache(groupPath)
    const { cachedRows } = dataCache

    const col = columns.value[ctx.col]
    const rowObj = cachedRows.value.get(ctx.row)

    if (!col || !col?.columnObj || !rowObj || !col.isCellEditable || col.isSyncedColumn || rowObj.rowMeta?.isRlsHidden) return
    const columnObj = col.columnObj

    if (
      !columnObj ||
      isDataReadOnly.value ||
      !ctx ||
      !hasEditPermission.value ||
      columnObj.readonly ||
      (isSystemColumn(columnObj) && !isLinksOrLTAR(columnObj)) ||
      (!isLinksOrLTAR(columnObj) && isVirtualCol(columnObj)) ||
      isAutoGeneratedColumn(columnObj)
    ) {
      if (
        columnObj.readonly ||
        (isSystemColumn(columnObj) && !isLinksOrLTAR(columnObj)) ||
        (!isLinksOrLTAR(columnObj) && isVirtualCol(columnObj)) ||
        isAutoGeneratedColumn(columnObj)
      ) {
        message.toast(t('msg.info.computedFieldClearWarning'))
      }
      return
    }

    if (isVirtualCol(columnObj)) {
      // This will used to reload view data if it is self link column
      const isSelfLinkColumn = columnObj.fk_model_id === columnObj.colOptions?.fk_related_model_id

      if (isMMOrMMLike(columnObj) && rowObj) {
        await cleaMMCell(rowObj, columnObj)
      }

      if ((isBt(columnObj) || isOo(columnObj)) && !isMMOrMMLike(columnObj)) await clearLTARCell(rowObj, columnObj)

      if (isSelfLinkColumn) {
        reloadViewDataHook.trigger({ shouldShowLoading: false })
      }

      return
    }

    if (columnObj.title) {
      // handle Checkbox and rating fields in a special way
      switch (columnObj.uidt) {
        case UITypes.Checkbox:
          rowObj.row[columnObj.title] = false
          break
        case UITypes.Rating:
          rowObj.row[columnObj.title] = 0
          break
        default:
          rowObj.row[columnObj.title] = null
          break
      }
    }

    if (!skipUpdate) {
      // update/save cell value
      await updateOrSaveRow?.(rowObj, columnObj.title, undefined, undefined, undefined, groupPath)
    }
  }

  async function copyValue(ctx?: Cell, path: Array<number> = []) {
    try {
      if (selection.value.start !== null && selection.value.end !== null && !selection.value.isSingleCell()) {
        const cprows = await getRows(selection.value.start.row, selection.value.end.row, path)

        const cpcols = unref(fields).slice(selection.value.start.col, selection.value.end.col + 1) // slice the selected cols for copy

        await copyTable(cprows, cpcols)
        message.toast(
          t(`msg.toast.nCell${cprows.length * cpcols.length === 1 ? '' : 's'}Copied`, {
            n: cprows.length * cpcols.length,
          }),
        )
      } else {
        const dataCache = getDataCache(path)

        // if copy was called with context (right click position) - copy value from context
        // else if there is just one selected cell, copy it's value
        const cpRow = ctx?.row ?? activeCell.value.row
        const cpCol = ctx?.col ?? activeCell.value.column

        if (cpRow != null && cpCol != null) {
          const rowObj = unref(dataCache.cachedRows).get(cpRow)
          const columnObj = unref(fields)[cpCol]
          if (!rowObj || !columnObj) return

          const { textToCopy, cellValue, clipboardColumn, rowId } = valueToCopy(rowObj, columnObj, {
            meta: meta.value,
            metas: metas.value,
            isPg,
            isMysql,
          })

          const plainTextValue = isValidValue(textToCopy) ? textToCopy : ''

          await copy(plainTextValue)

          const clipboardItem: NcClipboardDataItemType = {
            dbCellValueArr: [[cellValue]],
            columns: [clipboardColumn],
            copiedPlainText: plainTextValue,
            rowIds: [rowId],
            tableId: meta.value?.id,
            id: getClipboardItemId(),
          }

          setCellClipboardDataItem(clipboardItem)

          message.toast(
            t(`msg.toast.nCellCopied`, {
              n: 1,
            }),
          )
        }
      }
    } catch (e) {
      console.log(e)
      message.error(t('msg.error.copyToClipboardError'))
    }
  }

  const handleAttachmentCellDrop = async (files: File[], attachmentCellDropOver: AttachmentCellDropOverType) => {
    if (isSqlView.value || isPublic.value || !hasEditPermission.value) return false

    if (!meta.value?.id) return

    const dataCache = getDataCache(attachmentCellDropOver.path)

    const { cachedRows } = dataCache

    const rowObj = (unref(cachedRows) as Map<number, Row>).get(attachmentCellDropOver.rowIndex!)
    const canvasGridColumn = (unref(columns) ?? [])[attachmentCellDropOver.colIndex]

    if (!rowObj || !canvasGridColumn || canvasGridColumn.readonly || !canvasGridColumn.columnObj) return

    const columnObj = canvasGridColumn.columnObj

    let dropValue: any

    try {
      try {
        dropValue = convertCellData(
          {
            value: '',
            to: columnObj.uidt as UITypes,
            column: columnObj,
            appInfo: unref(appInfo),
            files,
            oldValue: rowObj.row[columnObj.title!],
            maxAttachmentsAllowedInCell: maxAttachmentsAllowedInCell.value,
            showUpgradeToAddMoreAttachmentsInCell,
          },
          isMysql(meta.value?.source_id),
        )
        validateColumnValue(columnObj, dropValue)
      } catch (ex) {
        dropValue = null
      }

      if (!dropValue?.length) return

      const dropRowPk = extractPkFromRow(rowObj.row, meta.value?.columns as ColumnType[])
      if (!dropRowPk) return

      await actionManager.executeUploadAction(dropRowPk, columnObj.id!, [], async () => {
        const newAttachments =
          (await handleFileUploadAndGetCellValue(dropValue, columnObj.id!, rowObj.row[columnObj.title!])) || []

        const oldAttachments = ncIsArray(rowObj.row[columnObj.title!]) ? rowObj.row[columnObj.title!] : []

        rowObj.row[columnObj.title!] =
          newAttachments.length || oldAttachments.length ? JSON.stringify(oldAttachments.concat(newAttachments)) : null

        await syncCellData?.(
          {
            row: attachmentCellDropOver.rowIndex,
            column: attachmentCellDropOver.colIndex,
          },
          attachmentCellDropOver.path,
        )
      })
    } catch (ex) {
      console.log(ex)
      message.error(t('msg.error.errorOccuredWhileDroppingAttachments'))
    }
  }

  function handleParseAttachmentCellData<T>(value: T): T {
    const parsedVal = parseProp(value)

    if (parsedVal && Array.isArray(parsedVal)) {
      return parsedVal as T
    } else {
      return [] as T
    }
  }
  useEventListener(document, 'paste', handlePaste)
  return { copyValue, clearCell, isPasteable, handleAttachmentCellDrop }
}
